//frontend/src/App.tsx

import React, { useState, useEffect, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { getCsrfToken } from './utils/csrf';
import { authService, apiClient, notifySessionExpired } from './services/authService';
import i18n from './i18n';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import { HashRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

// 🔒 SECURITY: Configure axios globally to include CSRF token and credentials
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase() || '')) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['x-xsrf-token'] = csrfToken;
    }
  }
  return config;
}, (error) => Promise.reject(error));

// 🔒 SECURITY: Global response interceptor for handling 401 errors
// This interceptor catches 401s from direct axios calls (not apiClient) and triggers logout
// The authService.apiClient has its own interceptor with refresh logic - this is complementary
// Direct axios calls will trigger session expired modal on 401; apiClient calls will attempt refresh first

let isGlobalSessionExpiredNotified = false;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Skip auth endpoints to avoid infinite loops
    const isAuthEndpoint = originalRequest.url?.includes('/api/auth/');
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - show session expired modal for direct axios calls
    // Note: apiClient has its own refresh logic, this catches direct axios usage
    if (error.response?.status === 401 && !originalRequest._retry && !isGlobalSessionExpiredNotified) {
      originalRequest._retry = true;


      // Prevent multiple simultaneous notifications
      isGlobalSessionExpiredNotified = true;

      // Notify via event system - AuthContext will show modal
      // Reset flag after a short delay to allow future detections (e.g. after re-login)
      setTimeout(() => { isGlobalSessionExpiredNotified = false; }, 5000);

      // Trigger the session expired callback (registered by AuthContext)
      notifySessionExpired();

      // Return rejected promise to stop further processing
      return Promise.reject(new Error('Session expired'));
    }

    return Promise.reject(error);
  }
);

// 🔒 SECURITY: All authentication now uses httpOnly cookies exclusively
// Backend authMiddleware reads tokens from httpOnly cookies automatically (no Authorization header needed)
// Browser sends cookies automatically via withCredentials: true
// This prevents XSS attacks by keeping tokens out of JavaScript-accessible storage

import {
  MantineProvider,
  createTheme,
  Box,
  Container,
  Paper,
  Stack,
  ThemeIcon,
  Text,
  Loader,
  Center,
  Transition,
  rem,
  SimpleGrid,    // ✅ AGGIUNTO
  Alert,         // ✅ AGGIUNTO
  Card,          // ✅ AGGIUNTO
  Group,         // ✅ AGGIUNTO
  Badge,         // ✅ AGGIUNTO
  SegmentedControl // ✅ AGGIUNTO per toggle chatbots/agents
} from '@mantine/core';
import { Notifications } from '@mantine/notifications'; // ✅ AGGIUNTO per le notifiche
import { ModalsProvider } from '@mantine/modals'; // ✅ AGGIUNTO per le modali di conferma
import {
  IconSparkles,
  IconLock,
  IconAlertTriangle,
  IconInfoCircle,
  IconRobot,
  IconBrandGoogle,
  IconCpu // ✅ AGGIUNTO per icona Agents
} from '@tabler/icons-react';
import zeusLogo from './assets/Logo_Bollo_3_1761080793189.png';
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import Footer from './components/layout/Footer';
import sessionManager from './services/sessionManager';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/hooks/useAuth';
import PrivateRoute from '@/components/PrivateRoute';
import { PERMISSIONS } from '@/constants/permissions';
import ErrorBoundary from '@/components/ErrorBoundary';
import SessionExpiredModal from '@/components/SessionExpiredModal';
import { lazyWithRetry } from './utils/lazyWithRetry';
import McpLoginGate from './pages/McpLoginGate';

const HandoverDashboard = lazyWithRetry(() => import('./components/langchain/HandoverDashboard'));
const ChatMonitor = lazyWithRetry(() => import('./components/langchain/ChatMonitor'));
const GoogleIntegrationsManager = lazyWithRetry(() => import('./components/langchain/GoogleIntegrationsManager'));
const BotGoalsManager = lazyWithRetry(() => import('./components/langchain/goals/BotGoalsWrapper'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const HandoverReport = lazyWithRetry(() => import('./pages/HandoverReport'));
const Chatbots = lazyWithRetry(() => import('./pages/Chatbots'));
const ChatbotEditor = lazyWithRetry(() => import('./pages/chatbot-editor/ChatbotEditor'));
const Conversations = lazyWithRetry(() => import('./pages/Conversations'));
const Clients = lazyWithRetry(() => import('./pages/Clients'));
const WhatsAppDashboard = lazyWithRetry(() => import('./components/whatsapp/WhatsAppDashboard'));
const WhatsAppCloudDashboard = lazyWithRetry(() => import('./components/whatsapp-cloud/WhatsAppCloudDashboard'));
const WhatsAppContacts = lazyWithRetry(() => import('./pages/WhatsAppContacts'));
const CustomFields = lazyWithRetry(() => import('./pages/CustomFields'));
const AutomationsLayout = lazyWithRetry(() => import('./components/automations/AutomationsLayout'));
const IntegrationHub = lazyWithRetry(() => import('./components/integrations/IntegrationHub'));
const WhatsAppCloudTestSend = lazyWithRetry(() => import('./components/whatsapp-cloud/WhatsAppCloudTestSend'));
const InstagramDashboard = lazyWithRetry(() => import('./components/instagram/InstagramDashboard'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const PasswordResetRequest = lazyWithRetry(() => import("./components/PasswordResetRequest"));
const PasswordReset = lazyWithRetry(() => import("./components/PasswordReset"));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const McpConsent = lazyWithRetry(() => import('./pages/McpConsent'));
const McpConnections = lazyWithRetry(() => import('./pages/McpConnections'));
const DettaglioConversazione = lazyWithRetry(() => import('./components/DettaglioConversazione'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const Users = lazyWithRetry(() => import('./pages/Users'));

// Claude Agents Pages
const AgentsList = lazyWithRetry(() => import('./pages/agents/AgentsList'));
const AgentEditor = lazyWithRetry(() => import('./pages/agents/AgentEditor'));
const AgentAnalytics = lazyWithRetry(() => import('./pages/agents/AgentAnalytics'));
const AgentConversations = lazyWithRetry(() => import('./pages/agents/AgentConversations'));

// Leads Dashboard
const LeadsDashboard = lazyWithRetry(() => import('./pages/leads/LeadsDashboard'));

// Flow Builder
const FlowBuilderPage = lazyWithRetry(() => import('./pages/FlowBuilderPage'));

// WhatsApp Cloud Template Editor
const TemplateEditorPage = lazyWithRetry(() => import('./pages/whatsapp-cloud/TemplateEditorPage'));

// WhatsApp Templates standalone page (for chat_manager access)
const WhatsAppTemplatesPage = lazyWithRetry(() => import('./pages/whatsapp-cloud/WhatsAppTemplatesPage'));

// Drip Campaign Audit (Global Admin only)
const DripAuditLog = lazyWithRetry(() => import('./pages/DripAuditLog'));

// Audit Trail (admin_globale only)
const AuditTrail = lazyWithRetry(() => import('./pages/admin/AuditTrail'));

// Subscription Plans admin page (admin_globale only)
const SubscriptionPlans = lazyWithRetry(() => import('./pages/admin/SubscriptionPlans'));

// Fatture in Cloud config page (admin_globale only)
const FattureInCloudConfig = lazyWithRetry(() => import('./pages/admin/FattureInCloudConfig'));

// LLM Usage dashboard (admin_globale only)
const LlmUsageDashboard = lazyWithRetry(() => import('./pages/admin/LlmUsageDashboard'));
const RagObservabilityDashboard = lazyWithRetry(() => import('./pages/admin/RagObservabilityDashboard'));
const MetaGraphQuotaDashboard = lazyWithRetry(() => import('./pages/admin/MetaGraphQuotaDashboard'));

// Tema personalizzato Mantine epico
const theme = createTheme({
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  primaryColor: 'blue',
  defaultRadius: 'md',
  colors: {
    // Gradiente personalizzato per il brand
    brand: [
      '#f0f4ff',
      '#d9e5ff',
      '#b3ccff',
      '#8db3ff',
      '#2F3289',
      '#3a3d9e',
      '#4d5fc6',
      '#404fb4',
      '#333fa2',
      '#40266A'
    ],
  },
  components: {
    Container: {
      defaultProps: {
        sizes: {
          xs: 540,
          sm: 720,
          md: 960,
          lg: 1140,
          xl: 1320,
        },
      },
    },
    Button: {
      styles: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          }
        }
      }
    },
    Paper: {
      styles: {
        root: {
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
          }
        }
      }
    },
    Card: {
      styles: {
        root: {
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
          }
        }
      }
    }
  },
  headings: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: '700',
  },
  other: {
    // Gradiente brand per riutilizzo
    brandGradient: 'linear-gradient(45deg, #2F3289, #40266A)',
    glassBackground: 'rgba(255, 255, 255, 0.95)',
    glassBorder: '1px solid rgba(255, 255, 255, 0.2)',
    glassBlur: 'blur(10px)',
  }
});

// Componente Loading epico (inizializzazione app)
const EpicLoader = ({ visible }: { visible: boolean }) => (
  <Transition
    mounted={visible}
    transition="fade"
    duration={400}
    timingFunction="ease"
  >
    {(styles) => (
      <Box
        style={{
          ...styles,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          zIndex: 9999,
          overflow: 'hidden'
        }}
      >
        {/* Elementi animati di background */}
        <Box
          style={{
            position: 'absolute',
            top: '15%',
            left: '10%',
            width: '20px',
            height: '20px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <Box
          style={{
            position: 'absolute',
            top: '60%',
            right: '15%',
            width: '15px',
            height: '15px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '50%',
            animation: 'float 4s ease-in-out infinite 2s'
          }}
        />
        <Box
          style={{
            position: 'absolute',
            top: '25%',
            right: '20%',
            width: '25px',
            height: '25px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '50%',
            animation: 'float 8s ease-in-out infinite 1s'
          }}
        />

        <Container size="sm" style={{ position: 'relative', zIndex: 1 }}>
          <Center style={{ minHeight: '100vh' }}>
            <Paper
              shadow="xl"
              p="xl"
              radius="xl"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                textAlign: 'center'
              }}
            >
              <Stack gap="lg" align="center">
                <Box
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 2s infinite',
                    boxShadow: '0 4px 16px rgba(47, 50, 137, 0.2)'
                  }}
                >
                  <img 
                    src={zeusLogo} 
                    alt="Zeus Logo" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>

                <Stack gap="sm" align="center" mt="md">
                  <Stack gap={4} align="center">
                    <IconSparkles size={16} style={{ color: '#2F3289' }} />
                    <Text size="sm" c="dimmed" fw={500}>
                      Inizializzazione della piattaforma...
                    </Text>
                    <IconSparkles size={16} style={{ color: '#40266A' }} />
                  </Stack>

                  <Loader 
                    size="md" 
                    type="dots" 
                    color="blue"
                    style={{ marginTop: rem(16) }}
                  />
                </Stack>

                <Text size="xs" c="dimmed" mt="md">
                  Powered by Conversational Design @ 2025
                </Text>
              </Stack>
            </Paper>
          </Center>
        </Container>

        {/* CSS per animazioni */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
      </Box>
    )}
  </Transition>
);

// Componente PageLoader figo per navigazione tra pagine
const PageLoader = () => (
  <Center style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', position: 'relative', overflow: 'hidden' }}>
    {/* Particelle lightning animate */}
    <Box
      style={{
        position: 'absolute',
        top: '20%',
        left: '25%',
        width: '4px',
        height: '4px',
        background: 'linear-gradient(45deg, #2F3289, #40266A)',
        borderRadius: '50%',
        boxShadow: '0 0 10px rgba(47, 50, 137, 0.6)',
        animation: 'lightning 2s ease-in-out infinite'
      }}
    />
    <Box
      style={{
        position: 'absolute',
        top: '70%',
        right: '30%',
        width: '3px',
        height: '3px',
        background: 'linear-gradient(45deg, #40266A, #2F3289)',
        borderRadius: '50%',
        boxShadow: '0 0 8px rgba(64, 38, 106, 0.6)',
        animation: 'lightning 2.5s ease-in-out infinite 0.5s'
      }}
    />
    <Box
      style={{
        position: 'absolute',
        top: '40%',
        right: '20%',
        width: '5px',
        height: '5px',
        background: 'linear-gradient(45deg, #2F3289, #40266A)',
        borderRadius: '50%',
        boxShadow: '0 0 12px rgba(47, 50, 137, 0.8)',
        animation: 'lightning 1.8s ease-in-out infinite 1s'
      }}
    />

    <Paper
      shadow="md"
      p="xl"
      radius="lg"
      style={{
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(47, 50, 137, 0.1)',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1
      }}
    >
      <Stack gap="md" align="center">
        {/* Logo con effetto rotate e glow */}
        <Box
          style={{
            position: 'relative',
            width: '60px',
            height: '60px'
          }}
        >
          {/* Glow ring esterno */}
          <Box
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'linear-gradient(45deg, rgba(47, 50, 137, 0.3), rgba(64, 38, 106, 0.3))',
              animation: 'pulseGlow 2s ease-in-out infinite'
            }}
          />
          
          {/* Logo centrale */}
          <Box
            style={{
              position: 'relative',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'spinSlow 3s linear infinite',
              boxShadow: '0 4px 20px rgba(47, 50, 137, 0.3)',
              background: 'white'
            }}
          >
            <img 
              src={zeusLogo} 
              alt="Zeus Logo" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                animation: 'spinReverse 3s linear infinite'
              }}
            />
          </Box>

          {/* Lightning sparks */}
          <IconSparkles 
            size={12} 
            style={{ 
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              color: '#2F3289',
              animation: 'sparkle 1.5s ease-in-out infinite'
            }} 
          />
          <IconSparkles 
            size={10} 
            style={{ 
              position: 'absolute',
              bottom: '-3px',
              left: '-3px',
              color: '#40266A',
              animation: 'sparkle 1.5s ease-in-out infinite 0.75s'
            }} 
          />
        </Box>

        {/* Testo e loader */}
        <Stack gap={6} align="center">
          <Text 
            size="sm" 
            fw={600}
            style={{
              background: 'linear-gradient(45deg, #2F3289, #40266A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {i18n.t('common:common.loadingInProgress')}
          </Text>
          
          {/* Loader dots personalizzato */}
          <Group gap={6} justify="center">
            <Box
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #2F3289, #40266A)',
                animation: 'dotPulse 1.4s ease-in-out infinite'
              }}
            />
            <Box
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #2F3289, #40266A)',
                animation: 'dotPulse 1.4s ease-in-out infinite 0.2s'
              }}
            />
            <Box
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #2F3289, #40266A)',
                animation: 'dotPulse 1.4s ease-in-out infinite 0.4s'
              }}
            />
          </Group>
        </Stack>
      </Stack>
    </Paper>

    {/* CSS per animazioni custom */}
    <style>{`
      @keyframes lightning {
        0%, 100% { 
          opacity: 0.2;
          transform: translateY(0) scale(1);
        }
        50% { 
          opacity: 1;
          transform: translateY(-15px) scale(1.5);
        }
      }

      @keyframes pulseGlow {
        0%, 100% { 
          transform: translate(-50%, -50%) scale(1);
          opacity: 0.5;
        }
        50% { 
          transform: translate(-50%, -50%) scale(1.2);
          opacity: 0.8;
        }
      }

      @keyframes spinSlow {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @keyframes spinReverse {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(-360deg); }
      }

      @keyframes sparkle {
        0%, 100% { 
          opacity: 0.3;
          transform: scale(0.8) rotate(0deg);
        }
        50% { 
          opacity: 1;
          transform: scale(1.2) rotate(180deg);
        }
      }

      @keyframes dotPulse {
        0%, 100% { 
          transform: scale(1);
          opacity: 0.5;
        }
        50% { 
          transform: scale(1.5);
          opacity: 1;
        }
      }
    `}</style>
  </Center>
);

// ✅ NUOVO: Componente Wrapper per Google Integrations con controllo accessi
// Supporta sia Chatbots che Agents
const GoogleIntegrationsWrapper = () => {
  const [entityType, setEntityType] = useState<'chatbot' | 'agent'>('chatbot');
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [showSelector, setShowSelector] = useState(true);
  const [chatbots, setChatbots] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const { t } = useTranslation('common');
  const { role: userRole, canAccessGoogleIntegrations } = useAuth();

  // Carica lista chatbots e agents
  useEffect(() => {
    loadEntities();
  }, [entityType]);

  const loadEntities = async () => {
    setIsLoading(true);
    setError('');

    try {
      if (entityType === 'chatbot') {
        const response = await apiClient.get('/api/langchain/chatbots');
        setChatbots(response.data.data || []);
      } else {
        const response = await apiClient.get('/api/agents');
        setAgents(response.data.data || []);
      }
    } catch (err) {
      console.error(`Errore caricamento ${entityType}s:`, err);
      setError(t('common.loadError', { entityType }));
    } finally {
      setIsLoading(false);
    }
  };

  // Controllo accessi
  if (!canAccessGoogleIntegrations()) {
    return (
      <Container size="md" py={40}>
        <Paper p="xl" radius="lg" withBorder style={{ textAlign: 'center', background: '#fafbfc' }}>
          <Stack gap="lg" align="center">
            <ThemeIcon size={60} radius="xl" variant="light" color="red">
              <IconLock size={30} />
            </ThemeIcon>
            <div>
              <Text size="xl" fw={700} mb="xs">{t('common.accessDenied')}</Text>
              <Text c="dimmed" mb="lg">
                {t('common.noPermissionGoogleIntegrations')}
                <br />
                {t('common.onlyAdminsCanManage')}
              </Text>
              <Text size="sm" c="dimmed">
                {t('common.currentRole')} <Text component="span" fw={600}>{userRole}</Text>
                <br />
                {t('common.authorizedRoles')}
              </Text>
            </div>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Selector chatbot/agent
  if (showSelector && !selectedEntity) {
    const entities = entityType === 'chatbot' ? chatbots : agents;
    const emptyMessage = entityType === 'chatbot'
      ? t('common.noChatbotsAvailable')
      : t('common.noAgentsAvailable');

    return (
      <Container size="md" py={40}>
        <Paper p="xl" radius="lg" withBorder style={{ background: '#fafbfc' }}>
          <Stack gap="lg">
            <div style={{ textAlign: 'center' }}>
              <ThemeIcon
                size={60}
                radius="xl"
                variant="gradient"
                gradient={{ from: '#2F3289', to: '#40266A', deg: 45 }}
                mx="auto"
                mb="md"
              >
                <IconBrandGoogle size={30} />
              </ThemeIcon>
              <Text size="xl" fw={700} mb="xs">{t('sidebar.googleIntegrations')}</Text>
              <Text c="dimmed" mb="md">
                {t('common.testGoogleCalendar')}
              </Text>

              {/* Toggle Chatbots/Agents */}
              <SegmentedControl
                value={entityType}
                onChange={(value) => {
                  setEntityType(value as 'chatbot' | 'agent');
                  setSelectedEntity(null);
                }}
                data={[
                  {
                    value: 'chatbot',
                    label: (
                      <Group gap="xs" wrap="nowrap">
                        <IconRobot size={16} />
                        <span>Chatbots</span>
                      </Group>
                    )
                  },
                  {
                    value: 'agent',
                    label: (
                      <Group gap="xs" wrap="nowrap">
                        <IconCpu size={16} />
                        <span>Agents</span>
                      </Group>
                    )
                  }
                ]}
                styles={{
                  root: {
                    background: 'rgba(47, 50, 137, 0.05)',
                    border: '1px solid rgba(47, 50, 137, 0.15)'
                  }
                }}
              />
            </div>

            {isLoading ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : error ? (
              <Alert icon={<IconAlertTriangle size={16} />} color="red">
                {error}
              </Alert>
            ) : entities.length === 0 ? (
              <Alert icon={<IconInfoCircle size={16} />} color="blue">
                {emptyMessage}
              </Alert>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {entities.map((entity) => {
                  const entityId = entityType === 'chatbot' ? entity.uuid : entity.id;
                  const badgeLabel = entityType === 'chatbot' ? t('sidebar.chatbots') : t('sidebar.agents');
                  const badgeColor = entityType === 'chatbot' ? 'blue' : 'grape';
                  const iconComponent = entityType === 'chatbot'
                    ? <IconRobot size={20} />
                    : <IconCpu size={20} />;

                  return (
                    <Card
                      key={entityId}
                      p="md"
                      radius="md"
                      withBorder
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        background: entityType === 'chatbot'
                          ? 'linear-gradient(45deg, rgba(47, 50, 137, 0.03), rgba(64, 38, 106, 0.03))'
                          : 'linear-gradient(45deg, rgba(64, 38, 106, 0.03), rgba(47, 50, 137, 0.03))',
                        borderColor: entityType === 'chatbot'
                          ? 'rgba(47, 50, 137, 0.15)'
                          : 'rgba(64, 38, 106, 0.15)'
                      }}
                      styles={{
                        root: {
                          '&:hover': {
                            borderColor: entityType === 'chatbot'
                              ? 'rgba(47, 50, 137, 0.3)'
                              : 'rgba(64, 38, 106, 0.3)',
                            transform: 'translateY(-2px)',
                            boxShadow: entityType === 'chatbot'
                              ? '0 8px 25px rgba(47, 50, 137, 0.15)'
                              : '0 8px 25px rgba(64, 38, 106, 0.15)'
                          }
                        }
                      }}
                      onClick={() => setSelectedEntity(entity)}
                    >
                      <Group justify="space-between" mb="md">
                        <ThemeIcon size="lg" variant="light" color={badgeColor} radius="md">
                          {iconComponent}
                        </ThemeIcon>
                        <Badge color={badgeColor} variant="light" size="sm">
                          {badgeLabel}
                        </Badge>
                      </Group>
                      <Text fw={600} size="sm" mb="xs">{entity.name}</Text>
                      {entityType === 'chatbot' && (
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          Client: {entity.client_name || 'N/A'}
                        </Text>
                      )}
                      {entityType === 'agent' && entity.description && (
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          {entity.description}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed">
                        Creato: {new Date(entity.created_at).toLocaleDateString()}
                      </Text>
                    </Card>
                  );
                })}
              </SimpleGrid>
            )}
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Manager per entity selezionata
  if (selectedEntity) {
    return (
      <GoogleIntegrationsManager
        chatbot={entityType === 'chatbot' ? selectedEntity : undefined}
        agent={entityType === 'agent' ? selectedEntity : undefined}
        entityType={entityType}
        userRole={userRole}
        onClose={() => {
          setSelectedEntity(null);
          setShowSelector(true);
        }}
      />
    );
  }

  return null;
};

// ✅ Componente contenuto app (usa useAuth, deve essere dentro AuthProvider)
function AppContent(): JSX.Element {
  const { isAuthenticated, clearUser, refreshUser, sessionExpired, clearSessionExpired, selectedClientId } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [appMounted, setAppMounted] = useState(false);

  // 🔒 SECURITY: Session management now uses httpOnly cookies exclusively
  // sessionManager.initialize() removed - tokens no longer exposed to JavaScript
  // Backend authMiddleware handles authentication via httpOnly cookies automatically
  useEffect(() => {
    // Funzione per aggiornare le informazioni utente
    const refreshUserInfo = async () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = storedUser?.id || storedUser?.data?.id;
        if (userId) {
          // 🔒 SECURITY: Backend reads authentication from httpOnly cookie automatically
          // Uses apiClient (not bare axios) so token refresh is attempted on 401
          const response = await apiClient.get(`/api/users/${userId}`);
          if (response.data && response.data.data) {
            // Aggiorna le informazioni nel localStorage (non sensibile)
            localStorage.setItem('user', JSON.stringify(response.data.data));
            localStorage.setItem('userRole', response.data.data.role);
          }
        }
      } catch (error) {
        console.error('Errore durante l\'aggiornamento delle informazioni utente:', error);
      }
    };

    // Initial user info refresh
    refreshUserInfo();

    // Simula un caricamento minimo per mostrare il loader epico
    setTimeout(() => {
      setIsLoading(false);
      setAppMounted(true);
    }, 1000);

    // Aggiorna le informazioni utente ogni minuto
    const interval = setInterval(refreshUserInfo, 60000);

    return () => clearInterval(interval);
  }, []);

  // Funzione per gestire il login (usata da Login.tsx)
  const handleLogin = () => {
    // Aggiorna AuthContext dopo login
    refreshUser();
  };

  // BUG FIX: Deduplicated logout logic — delegates to authService.logout() as single source of truth
  const handleLogout = async () => {
    clearUser();
    sessionStorage.setItem('loggedOut', 'true');
    await authService.logout();
  };

  const toggleSidebar = () => {
    setSidebarVisible((prev) => !prev);
  };

  // Handle session expired modal: perform full logout and redirect
  const handleSessionExpiredGoToLogin = async () => {
    clearSessionExpired();
    clearUser();
    await authService.logout();
  };

  return (
    <Suspense fallback={<PageLoader />}>
        <Notifications
          position="top-right"
          zIndex={2077}
          limit={5}
          autoClose={5000}
          containerWidth={420}
        />
        {/* Session Expired Modal - shown as overlay when session expires */}
        <SessionExpiredModal
          opened={sessionExpired}
          onGoToLogin={handleSessionExpiredGoToLogin}
        />
        {/* Epic Loader */}
        <EpicLoader visible={isLoading} />

      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Transition
          mounted={appMounted && !isLoading}
          transition="fade"
          duration={400}
          timingFunction="ease"
        >
          {(styles) => (
            <div style={styles}>
              {isAuthenticated ? (
                <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
                  <Sidebar visible={sidebarVisible} />
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, height: "100%", overflow: "hidden" }}>
                    <TopBar onLogout={handleLogout} onToggleSidebar={toggleSidebar} sidebarVisible={sidebarVisible} />
                    <main key={`tenant-${selectedClientId ?? 'default'}`} style={{ flex: 1, overflow: "auto", background: "#f6f7fb" }}>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" />} />
                        <Route path="/login" element={<Navigate to="/dashboard" />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/handover-report" element={<HandoverReport />} />
                        <Route path="/topic-taxonomy" element={<Navigate to="/conversations?section=taxonomy" replace />} />
                        <Route path="/conversation-report" element={<Navigate to="/conversations?section=report" replace />} />
                        <Route path="/advanced/handover" element={<HandoverDashboard />} />
                        <Route path="/advanced/chat-monitor" element={<ChatMonitor />} />
                        <Route path="/chatbots/create" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <ChatbotEditor mode="create" />
                          </PrivateRoute>
                        } />
                        <Route path="/chatbots/:id/edit" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <ChatbotEditor mode="edit" />
                          </PrivateRoute>
                        } />
                        <Route path="/chatbots" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <Chatbots />
                          </PrivateRoute>
                        } />
                        <Route path="/chatbots/:id/goals" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <BotGoalsManager />
                          </PrivateRoute>
                        } />

                        {/* Claude Agents Routes */}
                        <Route path="/agents" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <AgentsList />
                          </PrivateRoute>
                        } />
                        <Route path="/agents/create" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <AgentEditor mode="create" />
                          </PrivateRoute>
                        } />
                        <Route path="/agents/:id/edit" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <AgentEditor mode="edit" />
                          </PrivateRoute>
                        } />
                        <Route path="/agents/:id/analytics" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CHATBOTS}>
                            <AgentAnalytics />
                          </PrivateRoute>
                        } />
                        <Route path="/agents/conversations" element={<AgentConversations />} />

                        {/* Leads Dashboard */}
                        <Route path="/leads" element={<LeadsDashboard />} />

                        {/* Flow Builder Routes - Solo admin_globale per ora */}
                        <Route path="/flow-builder" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.FLOW_BUILDER}>
                            <FlowBuilderPage />
                          </PrivateRoute>
                        } />
                        <Route path="/flow-builder/:chatbotId" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.FLOW_BUILDER}>
                            <FlowBuilderPage />
                          </PrivateRoute>
                        } />

                        <Route path="/conversations" element={<Conversations />} />
                        <Route path="/clients" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CLIENTS}>
                            <Clients />
                          </PrivateRoute>
                        } />
                        <Route path="/audit-trail" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.VIEW_AUDIT_TRAIL}>
                            <AuditTrail />
                          </PrivateRoute>
                        } />
                        <Route path="/admin/piani" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CLIENTS}>
                            <SubscriptionPlans />
                          </PrivateRoute>
                        } />
                        <Route path="/admin/fatturazione" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_CLIENTS}>
                            <FattureInCloudConfig />
                          </PrivateRoute>
                        } />
                        <Route path="/admin/llm-usage" element={
                          <PrivateRoute globalAdminOnly>
                            <LlmUsageDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/admin/rag-observability" element={
                          <PrivateRoute globalAdminOnly>
                            <RagObservabilityDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/admin/meta-graph-quota" element={
                          <PrivateRoute globalAdminOnly>
                            <MetaGraphQuotaDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_WHATSAPP}>
                            <WhatsAppDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/integrations" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_AUTOMATIONS}>
                            <IntegrationHub />
                          </PrivateRoute>
                        } />
                        <Route path="/automations/*" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_AUTOMATIONS}>
                            <AutomationsLayout />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp-cloud" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_WHATSAPP}>
                            <WhatsAppCloudDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/instagram" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_INSTAGRAM}>
                            <InstagramDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp-cloud/contacts" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.VIEW_WHATSAPP_CONTACTS}>
                            <WhatsAppContacts />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp-cloud/custom-fields" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_AUTOMATIONS}>
                            <CustomFields />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp-cloud/test-send" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_WHATSAPP}>
                            <WhatsAppCloudTestSend />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp-cloud/templates" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_WHATSAPP_TEMPLATES}>
                            <WhatsAppTemplatesPage />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp-cloud/templates/create" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_WHATSAPP_TEMPLATES}>
                            <TemplateEditorPage />
                          </PrivateRoute>
                        } />
                        <Route path="/whatsapp-cloud/templates/:templateId/edit" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_WHATSAPP_TEMPLATES}>
                            <TemplateEditorPage />
                          </PrivateRoute>
                        } />
                        <Route path="/users" element={
                          <PrivateRoute requiredPermission={PERMISSIONS.MANAGE_USERS}>
                            <Users />
                          </PrivateRoute>
                        } />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/settings/mcp" element={<PrivateRoute><McpConnections /></PrivateRoute>} />
                        <Route path="/mcp/consent/:requestId" element={<PrivateRoute><McpConsent /></PrivateRoute>} />
                        <Route path="/conversations/:id" element={<DettaglioConversazione />} />

                        {/* Drip Campaign Audit - Global Admin Only */}
                        <Route path="/drip-audit" element={
                          <PrivateRoute globalAdminOnly>
                            <DripAuditLog />
                          </PrivateRoute>
                        } />

                        {/* ✅ NUOVO: Route per Google Integrations */}
                        <Route 
                          path="/google-integrations" 
                          element={
                            <PrivateRoute requiredPermission={PERMISSIONS.GOOGLE_INTEGRATIONS}>
                              <GoogleIntegrationsWrapper />
                            </PrivateRoute>
                          } 
                        />

                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </main>
                    <Footer />
                  </div>
                </div>
              ) : (
                <Routes>
                  <Route path="/login" element={<Login onLogin={handleLogin} onLogout={handleLogout} />} />
                  <Route path="/forgot-password" element={<PasswordResetRequest />} />
                  <Route path="/reset-password" element={<PasswordReset />} />
                  <Route path="/mcp/consent/:requestId" element={<McpLoginGate />} />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              )}
            </div>
          )}
        </Transition>
      </Router>

      {/* CSS globali per il design epico */}
      <style>{`
        * {
          transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
        }

        /* Scrollbar personalizzata */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #2F3289, #40266A);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #3a3d9e, #4d307d);
        }

        /* Animazioni globali */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(47, 50, 137, 0.5); }
          50% { box-shadow: 0 0 20px rgba(47, 50, 137, 0.8); }
        }

        /* Utility classes per animazioni */
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.6s ease-out;
        }

        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }

        /* Focus outline personalizzato */
        .mantine-Button-root:focus-visible,
        .mantine-TextInput-input:focus,
        .mantine-PasswordInput-input:focus {
          outline: 2px solid rgba(47, 50, 137, 0.5) !important;
          outline-offset: 2px;
        }

        /* Miglioramenti per accessibilità */
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

// ✅ App component wrapper con AuthProvider e ErrorBoundary
export default function App(): JSX.Element {
  // Error handler for logging/monitoring
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log to console in development
    console.error('[App] Global error caught:', error.message);

    // In production, send to error monitoring service
    // Example: Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme}>
        <ModalsProvider>
          <ErrorBoundary onError={handleError}>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </ErrorBoundary>
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  );
}